import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import {
  FaBars,
  FaTimes,
  FaSun,
  FaMoon,
  FaChevronDown,
  FaGlobe,
  FaCode,
  FaMobileAlt,
  FaRobot,
  FaDatabase,
  FaBullhorn,
} from 'react-icons/fa';
import { trackEvent } from '../utils/analytics';
import { CTA_PRIMARY_PATH } from '../constants/cta';
import { useI18n } from '../i18n/I18nContext';

const MEGA_LINKS = {
  development: [
    {
      to: '/solutions/web-development',
      labelKey: 'nav.mega.webTitle',
      descKey: 'nav.mega.webDesc',
      Icon: FaCode,
    },
    {
      to: '/solutions/mobile-development',
      labelKey: 'nav.mega.mobileTitle',
      descKey: 'nav.mega.mobileDesc',
      Icon: FaMobileAlt,
    },
  ],
  aiData: [
    {
      to: '/solutions/ai-solutions',
      labelKey: 'nav.mega.aiTitle',
      descKey: 'nav.mega.aiDesc',
      Icon: FaRobot,
    },
    {
      to: '/solutions/data-engineering',
      labelKey: 'nav.mega.dataTitle',
      descKey: 'nav.mega.dataDesc',
      Icon: FaDatabase,
    },
  ],
  growth: [
    {
      to: '/solutions/digital-marketing',
      labelKey: 'nav.mega.mktTitle',
      descKey: 'nav.mega.mktDesc',
      Icon: FaBullhorn,
    },
  ],
};

function isDesktopViewport() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(min-width: 1024px)').matches;
}

const Navbar = () => {
  const { locale, setLocale, t } = useI18n();
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [theme, setTheme] = useState('light');
  const [solutionsOpen, setSolutionsOpen] = useState(false);
  const [solutionsMegaLayout, setSolutionsMegaLayout] = useState(null);
  const location = useLocation();
  const solutionsMenuRef = useRef(null);
  const solutionsTriggerRef = useRef(null);
  const solutionsPanelRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);

    const savedTheme = localStorage.getItem('theme') || 'light';
    setTheme(savedTheme);
    document.documentElement.setAttribute('data-theme', savedTheme);

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setSolutionsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        setSolutionsOpen(false);
      }
    };
    const onClickOutside = (e) => {
      if (!solutionsOpen) return;
      const t = e.target;
      if (solutionsMenuRef.current?.contains(t)) return;
      if (solutionsPanelRef.current?.contains(t)) return;
      setSolutionsOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onClickOutside);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onClickOutside);
    };
  }, [solutionsOpen]);

  useLayoutEffect(() => {
    if (!solutionsOpen) {
      setSolutionsMegaLayout(null);
      return undefined;
    }
    if (typeof window === 'undefined' || !window.matchMedia) {
      return undefined;
    }

    const mq = window.matchMedia('(min-width: 1024px)');

    const runLayout = () => {
      if (!mq.matches || !solutionsTriggerRef.current) {
        setSolutionsMegaLayout(null);
        return;
      }
      const r = solutionsTriggerRef.current.getBoundingClientRect();
      const vw = window.innerWidth;
      const margin = 16;
      const width = Math.min(920, vw - margin * 2);
      let left = r.left;
      if (left + width > vw - margin) {
        left = Math.max(margin, vw - margin - width);
      }
      setSolutionsMegaLayout({
        top: r.bottom + 10,
        left,
        width,
      });
    };

    runLayout();

    const schedule = () => {
      requestAnimationFrame(runLayout);
    };

    mq.addEventListener('change', schedule);
    window.addEventListener('resize', schedule);
    window.addEventListener('scroll', schedule, true);
    return () => {
      mq.removeEventListener('change', schedule);
      window.removeEventListener('resize', schedule);
      window.removeEventListener('scroll', schedule, true);
    };
  }, [solutionsOpen]);

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    document.documentElement.setAttribute('data-theme', newTheme);
  };

  const toggleLocale = () => {
    const next = locale === 'en' ? 'ur' : 'en';
    setLocale(next);
    trackEvent('locale_toggle', { locale: next });
  };

  const toggleMenu = () => setIsOpen((v) => !v);
  const closeMenu = () => {
    setIsOpen(false);
    setSolutionsOpen(false);
  };
  const isActive = (path) => location.pathname === path;
  const isSolutionsActive = location.pathname.startsWith('/solutions');

  const ctaLabel = t('nav.bookConsultation');
  const desktop = isDesktopViewport();

  const renderMegaColumn = (titleKey, items) => (
    <div className="nav-mega-col" key={titleKey}>
      <p className="nav-mega-heading">{t(titleKey)}</p>
      <ul className="nav-mega-list">
        {items.map((item) => {
          const Icon = item.Icon;
          return (
            <li key={item.to}>
              <Link
                to={item.to}
                className={`nav-mega-link ${isActive(item.to) ? 'active' : ''}`}
                onClick={() => {
                  trackEvent('nav_click', { target: `mega:${item.to}` });
                  closeMenu();
                }}
              >
                <span className="nav-mega-link-icon" aria-hidden>
                  <Icon />
                </span>
                <span className="nav-mega-link-text">
                  <span className="nav-mega-link-title">{t(item.labelKey)}</span>
                  <span className="nav-mega-link-desc">{t(item.descKey)}</span>
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );

  const megaPanelInner = (
    <div className="nav-mega-grid">
      {renderMegaColumn('nav.mega.development', MEGA_LINKS.development)}
      {renderMegaColumn('nav.mega.aiData', MEGA_LINKS.aiData)}
      {renderMegaColumn('nav.mega.growth', MEGA_LINKS.growth)}
      <div className="nav-mega-aside">
        <p className="nav-mega-aside-title">MSA</p>
        <p className="nav-mega-aside-text">{t('nav.mega.ctaHint')}</p>
        <Link
          to="/services"
          className="btn btn-outline btn-sm-mega"
          onClick={() => {
            trackEvent('nav_mega_view_all');
            closeMenu();
          }}
        >
          {t('nav.mega.viewAll')}
        </Link>
        <Link
          to={CTA_PRIMARY_PATH}
          className="btn btn-primary btn-sm-mega"
          onClick={() => {
            trackEvent('nav_mega_book');
            closeMenu();
          }}
        >
          {ctaLabel}
        </Link>
      </div>
    </div>
  );

  const megaPanelDesktop =
    solutionsOpen &&
    desktop &&
    solutionsMegaLayout &&
    typeof document !== 'undefined'
      ? createPortal(
          <div
            ref={solutionsPanelRef}
            id="solutions-mega-panel"
            className="nav-solutions-mega nav-solutions-mega--portaled open nav-solutions-mega--fixed"
            role="region"
            aria-label={t('nav.solutions')}
            style={{
              position: 'fixed',
              top: solutionsMegaLayout.top,
              left: solutionsMegaLayout.left,
              width: solutionsMegaLayout.width,
              right: 'auto',
              zIndex: 1250,
            }}
          >
            {megaPanelInner}
          </div>,
          document.body
        )
      : null;

  const megaPanelMobile =
    solutionsOpen && !desktop ? (
      <div
        ref={solutionsPanelRef}
        id="solutions-mega-panel"
        className={`nav-solutions-mega ${solutionsOpen ? 'open' : ''}`}
        role="region"
        aria-label={t('nav.solutions')}
      >
        {megaPanelInner}
      </div>
    ) : null;

  return (
    <>
      <nav className={`navbar ${isScrolled ? 'scrolled' : ''}`}>
        <div className="container">
          <div className="navbar-content">
            <Link to="/" className="navbar-brand" onClick={closeMenu}>
              <img src="/images/Logo.png" alt="MSA Technologies logo" />
              <span>MSA Technologies</span>
            </Link>

            <div
              className="nav-links-outer"
              aria-label="Main pages — scroll horizontally if items do not all fit"
            >
              <ul
                id="primary-navigation"
                className={`nav-links ${isOpen ? 'open' : ''}`}
                role="menubar"
              >
                <li role="none">
                  <Link
                    to="/"
                    role="menuitem"
                    className={isActive('/') ? 'active' : ''}
                    onClick={closeMenu}
                  >
                    {t('nav.home')}
                  </Link>
                </li>
                <li role="none">
                  <Link
                    to="/services"
                    role="menuitem"
                    className={isActive('/services') ? 'active' : ''}
                    onClick={() => {
                      trackEvent('nav_click', { target: 'services' });
                      closeMenu();
                    }}
                  >
                    {t('nav.services')}
                  </Link>
                </li>

                <li role="none" className="nav-item-with-submenu" ref={solutionsMenuRef}>
                  <button
                    type="button"
                    ref={solutionsTriggerRef}
                    className={`nav-submenu-toggle ${isSolutionsActive ? 'active' : ''}`}
                    aria-haspopup="true"
                    aria-expanded={solutionsOpen}
                    aria-controls="solutions-mega-panel"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSolutionsOpen((v) => !v);
                    }}
                  >
                    {t('nav.solutions')} <FaChevronDown aria-hidden="true" />
                  </button>
                  {megaPanelMobile}
                </li>

                <li role="none">
                  <Link
                    to="/products"
                    role="menuitem"
                    className={isActive('/products') ? 'active' : ''}
                    onClick={() => {
                      trackEvent('nav_click', { target: 'products' });
                      closeMenu();
                    }}
                  >
                    {t('nav.products')}
                  </Link>
                </li>
                <li role="none">
                  <Link
                    to="/portfolio"
                    role="menuitem"
                    className={isActive('/portfolio') ? 'active' : ''}
                    onClick={() => {
                      trackEvent('nav_click', { target: 'portfolio' });
                      closeMenu();
                    }}
                  >
                    {t('nav.portfolio')}
                  </Link>
                </li>
                <li role="none">
                  <Link
                    to="/team"
                    role="menuitem"
                    className={isActive('/team') ? 'active' : ''}
                    onClick={() => {
                      trackEvent('nav_click', { target: 'team' });
                      closeMenu();
                    }}
                  >
                    {t('nav.team')}
                  </Link>
                </li>
                <li role="none">
                  <Link
                    to="/contact"
                    role="menuitem"
                    className={isActive('/contact') ? 'active' : ''}
                    onClick={() => {
                      trackEvent('nav_click', { target: 'contact' });
                      closeMenu();
                    }}
                  >
                    {t('nav.contact')}
                  </Link>
                </li>
                <li role="none">
                  <Link
                    to={CTA_PRIMARY_PATH}
                    role="menuitem"
                    className={`btn btn-primary nav-cta-book ${isActive(CTA_PRIMARY_PATH) ? 'active' : ''}`}
                    onClick={() => {
                      trackEvent('nav_click', { target: 'book_consultation' });
                      closeMenu();
                    }}
                  >
                    {ctaLabel}
                  </Link>
                </li>
              </ul>
            </div>

            <div className="nav-controls">
              <button
                type="button"
                className="locale-toggle"
                onClick={toggleLocale}
                aria-label={t('nav.langAria')}
                title={locale === 'en' ? 'اردو' : 'English'}
              >
                <FaGlobe aria-hidden />
                <span className="locale-toggle-label">{t('nav.langShort')}</span>
              </button>
              <button
                type="button"
                className="theme-toggle"
                onClick={toggleTheme}
                aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
              >
                {theme === 'light' ? <FaMoon /> : <FaSun />}
              </button>

              <button
                type="button"
                className="mobile-menu-btn"
                onClick={toggleMenu}
                aria-label={isOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={isOpen}
                aria-controls="primary-navigation"
              >
                {isOpen ? <FaTimes /> : <FaBars />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {megaPanelDesktop}

      {isOpen && <div className="nav-backdrop" onClick={closeMenu} aria-hidden="true" />}
    </>
  );
};

export default Navbar;
